import { t } from "i18next";

import type { ShareableIncidentDataV1Response } from "@thalamos/common";
import { dayjs } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type {
  ExternalPatientLinkEnhanced,
  FormContextData,
} from "@thalamos/aspire-common";

import { api } from "~/api.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import { renderErrorToast } from "~/components/design-system/index.js";
import { logActionToDatadog } from "~/tracing.js";

export const generateMenuOptions = (
  formId: string,
  onView: (formId: string) => void,
  incidentId: string,
  formType:
    | "police-form"
    | "monitoring-form"
    | "s136-extension-form"
    | "outcome-form",
  pos: ShareableIncidentDataV1Response["placesOfSafety"],
  options?: {
    uploadToRioExternalLink?: ExternalPatientLinkEnhanced | null;
    uploadFormPdfViaExternalPatientLink?: (
      externalLink: ExternalPatientLinkEnhanced,
      formIds: string[],
      formContext: FormContextData,
    ) => Promise<void>;
    formContext: FormContextData;
    isFormCompleted?: boolean;
    uploadedToRio?: { uploaded: false } | { uploaded: true; date: string };
  },
): MenuOptionsType[] => {
  const placeOfSafety = pos[0];

  const {
    uploadToRioExternalLink,
    uploadFormPdfViaExternalPatientLink,
    formContext,
    isFormCompleted,
    uploadedToRio,
  } = options ?? {};

  return [
    {
      icon: "view",
      onClick: async () => {
        onView(formId);
        logActionToDatadog(`View PDF ${formType}`, {
          formId,
          incidentId,
          formType,
          placeOfSafety,
        });
      },
      name: "View PDF",
      disabled: false,
    },
    {
      icon: "download",
      onClick: async () => {
        const result = await api.forms.getPdf(formId, true);
        if (result.status === 200) {
          const dataUri = `data:application/pdf;base64,${result.data}`;
          triggerDownload(dataUri, "form.pdf");
          logActionToDatadog(`Download PDF ${formType}`, {
            formId,
            incidentId,
            formType,
            placeOfSafety,
          });
        } else {
          renderErrorToast({
            message: t("pages.policeIncidentFormPage.failedPdfDownload"),
          });
        }
      },
      name: "Download PDF",
      disabled: false,
    },
    ...(isFormCompleted &&
    uploadToRioExternalLink &&
    uploadFormPdfViaExternalPatientLink &&
    formContext
      ? [
          {
            name: "Upload to Rio",
            icon: uploadedToRio?.uploaded ? "uploadDone" : "upload",
            tooltip: uploadedToRio?.uploaded
              ? [
                  "Uploaded to Rio",
                  dayjs(uploadedToRio.date).format("DD/MM/YYYY HH:mm"),
                ]
              : undefined,
            onClick: async () => {
              await uploadFormPdfViaExternalPatientLink(
                uploadToRioExternalLink,
                [formId],
                formContext,
              );
            },
            disabled: false,
          },
        ]
      : []),
  ];
};
